import { useMemo } from 'react';
import {
  NumberParam,
  QueryParamConfigMap,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { FilterModel } from '../common/model/ui/filter.model';
import { OrderBy, SortDirection } from '../common/types';
import { hasAppliedListFilters } from '../common/utils/helpers';

export function useListQueryParams(
  filterDefinitions: FilterModel[],
  defaultOrderBy?: OrderBy
) {
  const config = useMemo<QueryParamConfigMap>(
    () => ({
      offset: withDefault(NumberParam, 0),
      order_by: StringParam,
      order_dir: StringParam,
      ...filterDefinitions.reduce<QueryParamConfigMap>(
        (filterConfig, filter) => ({
          ...filterConfig,
          [filter.fieldName]: withDefault(StringParam, defaultValue(filter)),
        }),
        {}
      ),
    }),
    [filterDefinitions]
  );
  const [params, setParams] = useQueryParams(config, {
    updateType: 'replaceIn',
  });

  const filters = filterDefinitions.map((filter) => ({
    ...filter,
    value: params[filter.fieldName] as string,
  }));

  const orderBy: OrderBy | undefined = params.order_by
    ? {
        order_by: params.order_by as string,
        order_dir: params.order_dir as SortDirection,
      }
    : defaultOrderBy;

  const filterQueryParams = filters.reduce<Record<string, string>>(
    (queryParams, filter) =>
      filter.value === ''
        ? queryParams
        : { ...queryParams, [filter.fieldName]: filter.value },
    {}
  );

  const changeFilter = ({ fieldName, value }: FilterModel) => {
    const definition = filterDefinitions.find(
      (filter) => filter.fieldName === fieldName
    );
    const encodedValue = String(value ?? '');

    setParams({
      offset: undefined,
      [fieldName]:
        definition && encodedValue === defaultValue(definition)
          ? undefined
          : encodedValue,
    });
  };

  const clearFilters = () =>
    setParams({
      offset: undefined,
      ...filterDefinitions.reduce<Record<string, undefined>>(
        (clearedFilters, filter) => ({
          ...clearedFilters,
          [filter.fieldName]: undefined,
        }),
        {}
      ),
    });

  const changeOrderBy = ({ order_by, order_dir }: OrderBy) =>
    setParams({ offset: undefined, order_by, order_dir });

  const setOffset = (offset: number) =>
    setParams({ offset: offset || undefined });

  return {
    offset: params.offset as number,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters: hasAppliedListFilters(filters),
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  };
}

function defaultValue(filter: FilterModel): string {
  return String(filter.value ?? '');
}
